import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Pagination, Panel, PanelFooter } from '@customerio/pluma-components/react';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { getRelatedCustomersQueryOptions } from '@/services/db/object/queries';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useObjectDetail } from './context';
import { relationshipKey } from './relationships/relationship-customer';
import { buildRelationshipRows } from './relationships/relationship-rows';
import { RelationshipsEmptyState } from './relationships/relationships-empty-state';
import { RelationshipsTableHeader } from './relationships/relationships-table-header';
import { RelationshipsTableSection } from './relationships/relationships-table-section';
import { useRelationshipsDeletion } from './relationships/use-relationships-deletion';
import { useRelationshipsSelection } from './relationships/use-relationships-selection';

const RELATIONSHIPS_PER_PAGE = 50;
/** The total is unknown until the first page resolves; the count copy hides on it. */
const UNKNOWN_TOTAL = -1;
const ANALYTICS_SOURCE = 'Object Profile';

export function ObjectRelationshipsTab() {
  const { record, workspaceId, canConfigureObjects, hrefs, onAddRelationships } = useObjectDetail();
  const { hasPermission } = usePermissions();
  const taxonomy = useProductTaxonomy();
  const [page, setPage] = useState(1);

  const cioObjectId = record.identifiers.cio_object_id;
  const personSingular = taxonomy.term('people.titleSingular');
  const personCountNoun = taxonomy.term('people.singular');
  const query = useQuery({
    ...getRelatedCustomersQueryOptions(workspaceId, cioObjectId, { page, size: RELATIONSHIPS_PER_PAGE }),
    // The count copy and the pagination keep their last known values while the
    // next page loads and the spinner replaces the table.
    placeholderData: keepPreviousData,
  });

  const relationshipsHref = hrefs.tabs.relationships;
  useRegisterBreadcrumbs(
    useMemo(
      () => [{ id: 'object.relationships', text: 'Relationships', href: relationshipsHref }],
      [relationshipsHref],
    ),
  );

  const relationships = query.data?.relationships ?? [];
  const total = query.data?.meta.pagination.total ?? UNKNOWN_TOTAL;
  const pageKeys = useMemo(() => relationships.map(relationshipKey), [relationships]);
  const selection = useRelationshipsSelection(pageKeys, total);

  const rows = buildRelationshipRows({
    relationships,
    customers: query.data?.customers ?? [],
    cioObjectId,
    workspaceId,
    personSingular,
    selectedKeys: selection.selectedKeys,
  });
  const deletion = useRelationshipsDeletion({
    workspaceId,
    cioObjectId,
    source: ANALYTICS_SOURCE,
    total,
    selectedRelationships: rows.filter((row) => row.isSelected).map((row) => row.relationship),
    onDeleted: selection.clear,
  });

  const changePage = (nextPage: number) => {
    setPage(nextPage);
    selection.clear();
  };

  return (
    <Panel withSectionBorders data-test-relationships-table>
      <RelationshipsTableHeader
        countNoun={personCountNoun}
        total={total}
        selectedCount={selection.selectedCount}
        isAllSelected={selection.isAllSelected}
        isPageSelected={selection.isPageSelected}
        canDelete={hasPermission('journeys/object_relationships/delete')}
        onClearSelection={selection.clear}
        onDeleteSelected={deletion.deleteSelected}
        onDeleteAll={deletion.deleteAll}
      />
      <RelationshipsTableSection
        isFetching={query.isFetching}
        isError={query.isError}
        rows={rows}
        personSingular={personSingular}
        canSelect={hasPermission('journeys/object_relationships/edit')}
        isPageSelected={selection.isPageSelected}
        isSelectionIndeterminate={selection.isIndeterminate}
        onSelectPage={selection.selectPage}
        onSelectRow={(relationship, index, checked) =>
          selection.selectRow(relationshipKey(relationship), index, checked)
        }
        onViewRelationship={undefined}
        emptyState={
          <RelationshipsEmptyState canAddRelationships={canConfigureObjects} onAddRelationships={onAddRelationships} />
        }
      />
      <PanelFooter justifyContent="flex-end">
        <Pagination totalItemCount={total} pageSize={RELATIONSHIPS_PER_PAGE} page={page} onPageChange={changePage} />
      </PanelFooter>
    </Panel>
  );
}
